import React from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { getTechnicianOnboarding } from "../api/technicians";
import {
  completeOpsCrossLaunch,
  completeMarketplaceCrossLaunch,
  getMySalons,
  getStaffSession,
  logoutStaffSession,
  selectSalon as selectSalonApi,
} from "../api/auth";
import { STAFF_SESSION_TOKEN } from "../api/base";
import { getRuntimeBrand } from "../config/runtimeBrand";
import { notifyOnboardingStatusChange } from "../utils/onboarding";

const AuthContext = createContext(null);
const STAFF_SESSION_REFRESH_INTERVAL_MS = 30 * 60 * 1000;

const LEGACY_AUTH_KEYS = [
  "token",
  "user_id",
  "full_name",
  "email",
  "role",
  "business_id",
  "business_code",
  "business_name",
  "is_super_admin",
  "superadmin_mode",
  "salon_id",
  "salon_name",
  "salon_timezone",
  "must_change_password",
  "accessible_salons",
];

function clearLegacyAuthStorage() {
  if (typeof window === "undefined") return;
  LEGACY_AUTH_KEYS.forEach((key) => window.localStorage.removeItem(key));
}

const toOptionalNumber = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

function normalizeUser(payload = {}) {
  if (!payload) return null;
  const renterProfile = payload.renter_profile ?? payload.renterProfile ?? null;
  const role = String(payload.role || "").toLowerCase();
  const hasGlobalAdminAccess =
    Boolean(payload.is_super_admin) ||
    role === "admin" ||
    role === "super_admin" ||
    role === "superadmin";
  return {
    id: toOptionalNumber(payload.user_id ?? payload.id),
    email: payload.email ?? null,
    full_name: payload.full_name ?? payload.name ?? "",
    role,
    business_id: toOptionalNumber(payload.business_id),
    business_code: payload.business_code ?? null,
    business_name: payload.business_name ?? null,
    photo_url:
      payload.photo_url ??
      payload.profile_photo_url ??
      payload.avatar_url ??
      null,
    salon_id: toOptionalNumber(payload.salon_id),
    salon_name: payload.salon_name ?? null,
    salon_timezone: payload.salon_timezone ?? null,
    is_super_admin: hasGlobalAdminAccess,
    superadmin_mode: Boolean(payload.superadmin_mode),
    must_change_password: Boolean(payload.must_change_password),
    marketplace_email_verified:
      payload.marketplace_email_verified === undefined
        ? null
        : Boolean(payload.marketplace_email_verified),
    billing_status: payload.billing_status ?? null,
    billing_access_granted:
      payload.billing_access_granted === undefined
        ? null
        : Boolean(payload.billing_access_granted),
    billing_card_required: Boolean(payload.billing_card_required),
    renter_profile: renterProfile,
    renter_profile_id: toOptionalNumber(renterProfile?.id),
    independent_operator_id:
      payload.independent_operator_id ?? renterProfile?.independent_operator_id ?? null,
    independent_operator_number: toOptionalNumber(
      payload.independent_operator_number ?? renterProfile?.independent_operator_number
    ),
    default_workspace_operator_mode:
      renterProfile?.default_workspace_operator_mode ?? null,
    default_client_ownership_mode:
      renterProfile?.default_client_ownership_mode ?? null,
    has_owner_access: Boolean(renterProfile?.has_owner_access),
    has_technician_access: Boolean(renterProfile?.has_technician_access),
  };
}

function getPendingLaunchToken() {
  if (typeof window === "undefined") return "";
  const params = new URLSearchParams(window.location.search || "");
  return String(params.get("launch_token") || "").trim();
}

function clearPendingLaunchToken() {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  if (!url.searchParams.has("launch_token")) {
    return;
  }
  url.searchParams.delete("launch_token");
  const nextPath = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState({}, "", nextPath || "/");
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessibleSalons, setAccessibleSalonsState] = useState([]);
  const [technicianStatus, setTechnicianStatusState] = useState(null);
  const lastOnboardingTokenRef = useRef(null);
  const authMutationVersionRef = useRef(0);
  const logoutInProgressRef = useRef(false);

  const persistTechnicianStatus = useCallback((status) => {
    if (!status) {
      setTechnicianStatusState(null);
      lastOnboardingTokenRef.current = null;
      if (typeof window !== "undefined") {
        window.localStorage.removeItem("technician_status");
      }
      return;
    }

    const staffSetupComplete =
      status.staff_setup_complete ?? status.setup_complete ?? null;

    const normalized = {
      profile_complete: Boolean(status.profile_complete),
      has_selected_salon: Boolean(status.has_selected_salon),
      staff_setup_complete: staffSetupComplete,
      setup_complete: Boolean(staffSetupComplete ?? false),
      salon_id: status.salon_id ?? null,
    };

    setTechnicianStatusState(normalized);
    if (typeof window !== "undefined") {
      window.localStorage.setItem("technician_status", JSON.stringify(normalized));
    }
  }, []);

  const updateAccessibleSalons = useCallback((list = []) => {
    setAccessibleSalonsState(Array.isArray(list) ? list : []);
  }, []);

  const updateCurrentUser = useCallback((partial = {}) => {
    if (!partial || typeof partial !== "object") return;
    setUser((prev) => ({ ...(prev || {}), ...partial }));
  }, []);

  const updateCurrentSalon = useCallback(
    (partialSalon = {}) => {
      if (!partialSalon || typeof partialSalon !== "object") return;
      const salonId =
        partialSalon.salon_id ?? partialSalon.id ?? partialSalon.salonId ?? null;
      const salonName =
        partialSalon.salon_name ??
        partialSalon.name ??
        partialSalon.salonName ??
        null;
      const salonTimezone =
        partialSalon.timezone ??
        partialSalon.salon_timezone ??
        partialSalon.salonTimezone ??
        null;

      const nextFields = {};
      if (salonId !== null) nextFields.salon_id = salonId;
      if (salonName !== null) nextFields.salon_name = salonName;
      if (salonTimezone !== null) nextFields.salon_timezone = salonTimezone;
      if (Object.keys(nextFields).length) {
        updateCurrentUser(nextFields);
      }

      if (salonId == null) return;
      const normalizedId = String(salonId);
      setAccessibleSalonsState((prev) =>
        prev.map((entry) => {
          const entryId = entry.id ?? entry.salon_id ?? entry.salonId;
          if (entryId == null || String(entryId) !== normalizedId) {
            return entry;
          }
          return {
            ...entry,
            name: salonName ?? entry.name ?? entry.salon_name ?? null,
            salon_name: salonName ?? entry.salon_name ?? entry.name ?? null,
            timezone: salonTimezone ?? entry.timezone ?? null,
          };
        })
      );
    },
    [updateCurrentUser]
  );

  const refreshTechnicianOnboarding = useCallback(
    async ({ accessToken, salonId } = {}) => {
      const authToken = accessToken || token || STAFF_SESSION_TOKEN;
      if (!authToken) return null;
      try {
        const onboarding = await getTechnicianOnboarding(authToken, salonId);
        if (!onboarding) return null;
        if (Array.isArray(onboarding.accessible_salons)) {
          updateAccessibleSalons(onboarding.accessible_salons);
        }
        persistTechnicianStatus({
          profile_complete: Boolean(onboarding.profile_complete),
          staff_setup_complete: onboarding.staff_setup_complete ?? null,
          setup_complete: Boolean(onboarding.staff_setup_complete ?? false),
          has_selected_salon: Boolean(onboarding.has_selected_salon),
          salon_id: onboarding.salon_id ?? null,
        });
        lastOnboardingTokenRef.current = authToken;
        return onboarding;
      } catch (err) {
        console.error("Unable to refresh technician onboarding", err);
        return null;
      }
    },
    [persistTechnicianStatus, token, updateAccessibleSalons]
  );

  const login = useCallback(
    (_newToken, extraUserData = {}) => {
      authMutationVersionRef.current += 1;
      clearLegacyAuthStorage();
      setToken(_newToken || STAFF_SESSION_TOKEN);
      setUser(normalizeUser(extraUserData));
      setLoading(false);
      const accessibleList =
        extraUserData.salons ??
        extraUserData.accessibleSalons ??
        extraUserData.accessible_salons ??
        [];
      updateAccessibleSalons(accessibleList);

      if ((extraUserData.role || "").toLowerCase() === "technician") {
        persistTechnicianStatus(extraUserData.technician_status ?? null);
      } else {
        persistTechnicianStatus(null);
      }
    },
    [persistTechnicianStatus, updateAccessibleSalons]
  );

  const clearAuthState = useCallback(() => {
    persistTechnicianStatus(null);
    setUser(null);
    setToken(null);
    setAccessibleSalonsState([]);
    setLoading(false);
    clearLegacyAuthStorage();
    notifyOnboardingStatusChange(false);
  }, [persistTechnicianStatus]);

  const logout = useCallback(async () => {
    authMutationVersionRef.current += 1;
    logoutInProgressRef.current = true;
    clearAuthState();
    try {
      await logoutStaffSession();
    } catch (err) {
      console.warn("Unable to complete staff logout request", err);
    } finally {
      authMutationVersionRef.current += 1;
      logoutInProgressRef.current = false;
      clearAuthState();
    }
  }, [clearAuthState]);

  const refreshSession = useCallback(async ({ quiet = false } = {}) => {
    if (logoutInProgressRef.current) {
      if (!quiet) {
        setLoading(false);
      }
      return null;
    }
    const pendingLaunchToken = getPendingLaunchToken();
    if (token && token !== STAFF_SESSION_TOKEN && !pendingLaunchToken) {
      if (!quiet) {
        setLoading(false);
      }
      return user;
    }
    const hydrateVersion = authMutationVersionRef.current;
    clearLegacyAuthStorage();
    if (!quiet) {
      setLoading(true);
    }
    try {
      if (pendingLaunchToken) {
        try {
          const runtimeBrand = getRuntimeBrand();
          if (runtimeBrand?.isMarketplaceHost) {
            await completeMarketplaceCrossLaunch(pendingLaunchToken);
          } else {
            await completeOpsCrossLaunch(pendingLaunchToken);
          }
        } catch (err) {
          console.warn("Unable to complete cross launch", err);
        } finally {
          clearPendingLaunchToken();
        }
      }
      if (authMutationVersionRef.current !== hydrateVersion) {
        return null;
      }
      if (logoutInProgressRef.current) {
        return null;
      }
      const session = await getStaffSession();
      if (authMutationVersionRef.current !== hydrateVersion) {
        return null;
      }
      if (logoutInProgressRef.current) {
        return null;
      }
      if (!session) {
        setToken(null);
        setUser(null);
        setAccessibleSalonsState([]);
        persistTechnicianStatus(null);
        return null;
      }

      const salonsResponse = await getMySalons(STAFF_SESSION_TOKEN).catch(() => null);
      if (authMutationVersionRef.current !== hydrateVersion) {
        return null;
      }
      if (logoutInProgressRef.current) {
        return null;
      }

      setToken(STAFF_SESSION_TOKEN);
      setUser(normalizeUser(session));
      setAccessibleSalonsState(salonsResponse?.salons || []);

      if ((session.role || "").toLowerCase() === "technician") {
        await refreshTechnicianOnboarding({ accessToken: STAFF_SESSION_TOKEN });
      } else {
        persistTechnicianStatus(null);
      }
      return session;
    } catch (err) {
      console.warn("Unable to hydrate staff session", err);
      setToken(null);
      setUser(null);
      setAccessibleSalonsState([]);
      persistTechnicianStatus(null);
      return null;
    } finally {
      if (
        !quiet &&
        authMutationVersionRef.current === hydrateVersion &&
        !logoutInProgressRef.current
      ) {
        setLoading(false);
      }
    }
  }, [persistTechnicianStatus, refreshTechnicianOnboarding, token, user]);

  const selectSalon = useCallback(
    async (salonId) => {
      const response = await selectSalonApi(salonId, token);
      login(response?.access_token, {
        ...response,
        accessibleSalons: accessibleSalons,
        salons: accessibleSalons,
      });
      return response;
    },
    [accessibleSalons, login, token]
  );

  useEffect(() => {
    refreshSession().catch(() => {});
  }, [refreshSession]);

  useEffect(() => {
    if (!token || !user || loading || token !== STAFF_SESSION_TOKEN) {
      return undefined;
    }
    if (typeof window === "undefined") {
      return undefined;
    }

    const refreshActiveSession = () => {
      if (logoutInProgressRef.current) return;
      if (
        typeof document !== "undefined" &&
        document.visibilityState === "hidden"
      ) {
        return;
      }
      refreshSession({ quiet: true }).catch(() => {});
    };
    const handleVisibilityChange = () => {
      if (
        typeof document !== "undefined" &&
        document.visibilityState === "visible"
      ) {
        refreshActiveSession();
      }
    };

    const intervalId = window.setInterval(
      refreshActiveSession,
      STAFF_SESSION_REFRESH_INTERVAL_MS
    );
    window.addEventListener("focus", refreshActiveSession);
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", handleVisibilityChange);
    }

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", refreshActiveSession);
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
      }
    };
  }, [loading, refreshSession, token, user]);

  const role = user?.role || null;

  useEffect(() => {
    const normalizedRole = (role || "").toLowerCase();
    if (!token || normalizedRole !== "technician") return;
    if (lastOnboardingTokenRef.current === token) return;
    refreshTechnicianOnboarding();
  }, [role, token, refreshTechnicianOnboarding]);

  const isAuthenticated = Boolean(token && user);
  const isSuperAdmin = Boolean(user?.is_super_admin);

  const currentSalon = useMemo(() => {
    const salonId = user?.salon_id ?? null;
    if (!salonId) return null;
    const normalizedId = String(salonId);
    return (
      accessibleSalons.find((entry) => {
        const entryId = entry.id ?? entry.salon_id ?? entry.salonId;
        return entryId != null && String(entryId) === normalizedId;
      }) || {
        id: salonId,
        salon_id: salonId,
        name: user?.salon_name ?? null,
        salon_name: user?.salon_name ?? null,
        timezone: user?.salon_timezone ?? null,
      }
    );
  }, [accessibleSalons, user?.salon_id, user?.salon_name, user?.salon_timezone]);

  const salonTimezone = currentSalon?.timezone || user?.salon_timezone || null;

  const value = useMemo(
    () => ({
      user,
      token,
      role,
      is_super_admin: isSuperAdmin,
      isAuthenticated,
      loading,
      login,
      logout,
      accessibleSalons,
      currentSalon,
      salonTimezone,
      setAccessibleSalons: updateAccessibleSalons,
      technicianStatus,
      refreshTechnicianOnboarding,
      refreshSession,
      selectSalon,
      updateCurrentUser,
      updateCurrentSalon,
    }),
    [
      accessibleSalons,
      currentSalon,
      isAuthenticated,
      isSuperAdmin,
      loading,
      login,
      logout,
      refreshTechnicianOnboarding,
      refreshSession,
      role,
      salonTimezone,
      selectSalon,
      technicianStatus,
      token,
      updateAccessibleSalons,
      updateCurrentSalon,
      updateCurrentUser,
      user,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}
